import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { UsersView } from '@futrli/views/views/users/UsersView/UsersView';

interface DeleteUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  workspaceId: string;
}

export const DeleteUserDialog = ({ isOpen, onClose, userId, workspaceId }: DeleteUserDialogProps) => {
  const intl = useIntl();
  const view = useApiView(useMemo(() => new UsersView(workspaceId), [workspaceId]));

  const { screen, handleAction, handleClose } = useDialogWizardState({
    error: view.state.errorDeletingUsers,
    isLoading: view.state.isLoading || view.state.isDeletingUsers,
    onAction: () => view.deleteMembers([userId]),
    onClose
  });

  return (
    <DeleteDialogWizard
      cancelButtonText={intl.formatMessage({
        id: 'common.cancel',
        defaultMessage: 'Cancel'
      })}
      dataTestId="delete-user-dialog"
      deleteButtonText={intl.formatMessage({
        id: 'common.sure_delete_user',
        defaultMessage: "I'm sure. Delete this user."
      })}
      deleteDescription={intl.formatMessage({
        id: 'predict.workspaces.users.delete.description',
        defaultMessage: 'Deleting this user will remove their access to this workspace. This action is irreversible.'
      })}
      errorMessage={view.state.errorDeletingUsers?.message}
      isOpen={isOpen}
      loadingMessage={
        view.state.isLoading
          ? intl.formatMessage({
              id: 'common.loading_please_wait',
              defaultMessage: 'Loading, please wait...'
            })
          : intl.formatMessage({
              id: 'predict.workspaces.users.delete.deleting',
              defaultMessage: 'Deleting user...'
            })
      }
      onClose={handleClose}
      onDelete={handleAction}
      screen={screen}
      successMessage={intl.formatMessage({
        id: 'predict.workspaces.users.delete.deleted',
        defaultMessage: 'User deleted'
      })}
      title={intl.formatMessage({
        id: 'common.warning_irreversable_process',
        defaultMessage: 'Warning: this process is irreversible'
      })}
    />
  );
};
